import React from 'react';
import { CreditRepairClient } from '../../types';
import { StandardButton } from '../../components/standard';
import { useCreditRepairClients } from '../../services/queries/creditManagementQueries';

interface CreditRepairDashboardProps {
  onSelectClient: (client: CreditRepairClient) => void;
  onBackToDashboard: () => void;
}

const CreditRepairDashboard: React.FC<CreditRepairDashboardProps> = ({ onSelectClient, onBackToDashboard }) => {
  const { data: clients = [] } = useCreditRepairClients();
  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center text-white text-lg">🔧</div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Credit Repair Cases</h1>
              <p className="text-gray-600 dark:text-gray-400">Manage negative item disputes and credit improvement</p>
            </div>
          </div>
          <StandardButton
            variant="secondary"
            onClick={onBackToDashboard}
          >
            Back to Dashboard
          </StandardButton>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg text-center">
            <h3 className="font-semibold text-blue-900 dark:text-blue-300">In Progress</h3>
            <p className="text-2xl font-bold text-blue-800 dark:text-blue-400">1</p>
          </div>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg text-center">
            <h3 className="font-semibold text-yellow-900 dark:text-yellow-300">Waiting for Disputes</h3>
            <p className="text-2xl font-bold text-yellow-800 dark:text-yellow-400">1</p>
          </div>
          <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg text-center">
            <h3 className="font-semibold text-orange-900 dark:text-orange-300">Due for Update</h3>
            <p className="text-2xl font-bold text-orange-800 dark:text-orange-400">0</p>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Client</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Credit Score</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Improvement</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
              {clients.map((client) => (
                <tr key={client.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{client.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                      client.status === 'In Progress'
                        ? 'bg-blue-100 text-blue-800'
                        : client.status === 'Waiting for Disputes'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-orange-100 text-orange-800'
                    }`}>
                      {client.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      <span className="text-red-600 dark:text-red-400">{client.initialScore}</span>
                      <span className="mx-2">→</span>
                      <span className="text-green-600 dark:text-green-400 font-semibold">{client.currentScore}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-green-600 dark:text-green-400 font-semibold">+{client.improvement}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => onSelectClient(client)}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
                    >
                      View Details →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CreditRepairDashboard;